import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useId,
} from 'react';
import classNames from 'classnames';

const DropdownCtx = createContext(null);

function useDropdownCtx() {
  const ctx = useContext(DropdownCtx);
  if (!ctx) {
    throw new Error('Dropdown subcomponents must be used inside a Dropdown');
  }
  return ctx;
}

export function Dropdown({
  isOpen,
  toggle: toggleProp,
  children,
  className,
  tag: Tag = 'div',
  nav,
  inNavbar,
  ...rest
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = typeof isOpen === 'boolean';
  const open = controlled ? isOpen : internalOpen;
  const rootRef = useRef(null);
  const menuId = useId();

  const toggle = useCallback(() => {
    if (!controlled) setInternalOpen((v) => !v);
    if (toggleProp) toggleProp();
  }, [controlled, toggleProp]);

  const close = useCallback(() => {
    if (!controlled) setInternalOpen(false);
    else if (toggleProp) toggleProp();
  }, [controlled, toggleProp]);

  useEffect(() => {
    if (!open) return;
    const onDocPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  return (
    <DropdownCtx.Provider
      value={{ open, toggle, close, nav, inNavbar, menuId }}
    >
      <Tag
        ref={rootRef}
        {...rest}
        className={classNames(
          nav ? 'nav-item relative' : 'relative inline-block',
          className
        )}
      >
        {children}
      </Tag>
    </DropdownCtx.Provider>
  );
}

export function UncontrolledDropdown({ children, ...rest }) {
  return <Dropdown {...rest}>{children}</Dropdown>;
}

const BTN_BASE =
  'inline-block cursor-pointer select-none border text-center align-middle font-normal leading-normal no-underline transition-colors duration-150 ease-in-out focus:outline-none disabled:pointer-events-none disabled:opacity-65 rounded px-5 py-2.5 text-base';

const BTN_VARIANTS = {
  primary:
    'border-primary bg-primary text-white hover:border-primary-hover hover:bg-primary-hover',
  secondary:
    'border-secondary bg-secondary text-white hover:border-primary-hover hover:bg-primary-hover',
  'outline-secondary':
    'border-secondary bg-transparent text-secondary hover:bg-secondary hover:text-white',
  'outline-gray':
    'border-gray-500 bg-transparent text-gray-700 hover:bg-gray-200',
  'outline-opaque-secondary':
    'border-secondary bg-white/70 text-secondary hover:bg-secondary hover:text-white',
  link:
    'border-transparent bg-transparent text-info hover:underline hover:text-[color-mix(in_oklab,var(--color-info),black_15%)]',
};

export function DropdownToggle({
  children,
  className,
  caret = false,
  nav = false,
  tag,
  color,
  onClick,
  ...rest
}) {
  const { open, toggle, menuId } = useDropdownCtx();
  const Tag = tag || (nav ? 'a' : 'button');

  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (e.defaultPrevented) return;
    toggle();
  };

  const variant = color || 'outline-secondary';

  return (
    <Tag
      {...rest}
      type={Tag === 'button' ? rest.type || 'button' : rest.type}
      aria-haspopup="menu"
      aria-expanded={open ? 'true' : 'false'}
      aria-controls={menuId}
      onClick={handleClick}
      className={classNames(
        'cursor-pointer focus:outline-none',
        nav
          ? 'block px-3 py-2 no-underline hover:underline'
          : classNames(BTN_BASE, BTN_VARIANTS[variant] || BTN_VARIANTS.primary),
        className
      )}
    >
      {children}
      {caret && (
        <svg
          aria-hidden="true"
          viewBox="0 0 10 6"
          className="ml-2 inline-block w-[0.6em] h-[0.6em] align-middle fill-none stroke-current"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="1,1 5,5 9,1" />
        </svg>
      )}
    </Tag>
  );
}

export function DropdownMenu({
  children,
  className,
  right = false,
  end = false,
  ...rest
}) {
  const { open, menuId } = useDropdownCtx();
  if (!open) return null;
  const alignEnd = right || end;
  return (
    <div
      id={menuId}
      role="menu"
      {...rest}
      className={classNames(
        'absolute top-full z-[1000] mt-0.5 min-w-[14rem] border border-black/15 bg-gray-100 py-2 shadow-[0_0.5rem_1rem_rgba(0,0,0,0.175)]',
        alignEnd ? 'right-0' : 'left-0',
        className
      )}
    >
      {children}
    </div>
  );
}

export function DropdownItem({
  children,
  className,
  onClick,
  header = false,
  divider = false,
  disabled = false,
  tag: Tag = 'button',
  ...rest
}) {
  const { close } = useDropdownCtx();

  if (divider) {
    return (
      <div
        role="separator"
        className={classNames('my-2 h-px bg-gray-300', className)}
      />
    );
  }
  if (header) {
    return (
      <div
        {...rest}
        className={classNames(
          'block px-6 py-1 text-sm text-gray-600',
          className
        )}
      >
        {children}
      </div>
    );
  }

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    if (onClick) onClick(e);
    if (!e.defaultPrevented) close();
  };

  return (
    <Tag
      role="menuitem"
      type={Tag === 'button' ? 'button' : undefined}
      disabled={disabled || undefined}
      aria-disabled={disabled || undefined}
      onClick={handleClick}
      {...rest}
      className={classNames(
        'block w-full cursor-pointer border-0 bg-transparent px-6 py-1 text-left text-gray-900 hover:bg-info hover:text-gray-100 focus:bg-info focus:text-gray-100 focus:outline-none disabled:cursor-default disabled:text-gray-600 disabled:hover:bg-transparent disabled:hover:text-gray-600',
        className
      )}
    >
      {children}
    </Tag>
  );
}
